/** Brand page — see mobile/lib/brand.ts and mobile/lib/brand-routes.ts for the source of truth. */
import { router, useLocalSearchParams } from "expo-router";
import { useState } from "react";
import {
  Platform,
  ScrollView,
  StatusBar,
  View,
  useWindowDimensions,
  type ViewStyle,
} from "react-native";

import { BrandAtoms, FOOTER_HEIGHT } from "../components/brand/BrandAtoms";
import {
  AdminCaseReviewDemo,
  AdminGlobalSearchDemo,
  LoadingScreenDemo,
  NotificationOverlayDemo,
  StandardOverlayDemo,
  ViewAsBannerDemo,
} from "../components/brand/DemoOverlays";
import {
  HeaderBar,
  HEADER_BG,
  PAGE_BG,
  type DemoKey,
  type DeviceMode,
} from "../components/brand/HeaderBar";
import { LogoOverlay } from "../components/brand/LogoOverlay";
import { PANE_THEMES } from "../components/brand/paneThemes";
import { RouteOverlay } from "../components/brand/RouteOverlay";
import { StandardPane } from "../components/brand/StandardPane";
import { type PaneKey } from "../lib/brand-routes";
import { useMarketingFonts } from "../lib/useMarketingFonts";

const PANES = ["General", "Student", "Client", "Admin", "Judge"] as const;
const HEADER_BORDER = "rgba(181,135,58,0.55)";

const PHONE_FRAME_WIDTH = 380;
const DESKTOP_FRAME_MAX = 1440;
const DESKTOP_FRAME_INSET = 48;

export default function BrandPage() {
  const { width } = useWindowDimensions();
  const fontsReady = useMarketingFonts();
  const params = useLocalSearchParams<{ pane?: string }>();
  const initialPane: PaneKey = (PANES as readonly string[]).includes(params.pane ?? "")
    ? (params.pane as PaneKey)
    : "General";
  const initialDevice: DeviceMode = width < 768 ? "phone" : "desktop";
  const [pane, setPane] = useState<PaneKey>(initialPane);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [logoOpen, setLogoOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState<DemoKey | null>(null);
  const [device, setDevice] = useState<DeviceMode>(initialDevice);

  const onDemoSelect = (key: DemoKey) => {
    setDemoOpen((prev) => (prev === key ? null : key));
  };

  const choosePane = (next: PaneKey) => {
    setPane(next);
    router.setParams({ pane: next });
  };

  if (!fontsReady) {
    return <View style={{ flex: 1, backgroundColor: PAGE_BG }} />;
  }

  const frameWidth =
    device === "phone"
      ? PHONE_FRAME_WIDTH
      : Math.min(width - DESKTOP_FRAME_INSET * 2, DESKTOP_FRAME_MAX);
  const paneWide = frameWidth >= 900;
  const panePad =
    frameWidth >= 1100 ? 64 : frameWidth >= 700 ? 40 : frameWidth >= 480 ? 24 : 18;
  const frameRadius = device === "phone" ? 28 : 10;
  const theme = PANE_THEMES[pane];

  return (
    <View style={{ flex: 1, backgroundColor: PAGE_BG }}>
      <StatusBar barStyle="light-content" />

      <HeaderBar
        pane={pane}
        onPaneChange={choosePane}
        demo={demoOpen}
        onDemoSelect={onDemoSelect}
        device={device}
        onDeviceChange={setDevice}
        onBrandingOpen={() => setOverlayOpen(true)}
        onLogoOpen={() => setLogoOpen(true)}
      />

      <ScrollView
        contentContainerStyle={{
          alignItems: "center",
          paddingTop: 40,
          paddingHorizontal: 20,
        }}
      >
        {/* Aspects frame */}
        <View
          style={[
            {
              width: frameWidth,
              borderRadius: frameRadius,
              overflow: "hidden",
              borderWidth: 1,
              borderColor: "rgba(247,244,238,0.07)",
              backgroundColor: HEADER_BG,
              marginBottom: 28,
            },
            Platform.OS === "web"
              ? ({ boxShadow: "0 24px 60px rgba(0,0,0,0.45)" } as unknown as ViewStyle)
              : null,
          ]}
        >
          <StandardPane theme={theme} wide={paneWide} pad={panePad} />
        </View>

        {/* Brand atoms footer — mirrors HeaderBar's cut-off chrome, but lives
            at the end of the scroll so it only appears when the reader has
            finished the aspects panel. Bottom edge has no border / no
            radius so once it's docked at the bottom of the viewport, it
            reads as "cut off" by the screen edge. */}
        <View
          style={{
            width: "100%",
            paddingHorizontal: 0,
          }}
        >
          <View
            style={{
              backgroundColor: HEADER_BG,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              borderLeftWidth: 1,
              borderRightWidth: 1,
              borderTopWidth: 1,
              borderBottomWidth: 0,
              borderColor: HEADER_BORDER,
              overflow: "hidden",
              height: FOOTER_HEIGHT,
            }}
          >
            <BrandAtoms theme={theme} wide={paneWide} pad={panePad} />
          </View>
        </View>
      </ScrollView>

      <RouteOverlay
        visible={overlayOpen}
        onClose={() => setOverlayOpen(false)}
        activePane={pane}
      />
      <LogoOverlay visible={logoOpen} onClose={() => setLogoOpen(false)} />
      <LoadingScreenDemo
        visible={demoOpen === "loading"}
        pane={pane}
        onClose={() => setDemoOpen(null)}
      />
      <StandardOverlayDemo
        visible={demoOpen === "standard"}
        pane={pane}
        onClose={() => setDemoOpen(null)}
      />
      <NotificationOverlayDemo
        visible={demoOpen === "notification"}
        pane={pane}
        onClose={() => setDemoOpen(null)}
      />
      <AdminCaseReviewDemo
        visible={demoOpen === "adminReview"}
        pane={pane}
        onClose={() => setDemoOpen(null)}
      />
      <AdminGlobalSearchDemo
        visible={demoOpen === "adminSearch"}
        pane={pane}
        onClose={() => setDemoOpen(null)}
      />
      <ViewAsBannerDemo
        visible={demoOpen === "viewAs"}
        pane={pane}
        onClose={() => setDemoOpen(null)}
      />
    </View>
  );
}
